/**
 * TYPO3 Authentication Service
 *
 * Handles authentication with the TYPO3 proxy (Ory-backed)
 * by validating session cookies and redirecting to TYPO3 login
 */

import { logger } from './logger';
import { readErrorBody } from './auth-utils';
import { notifySessionEnded, type SessionEndInfo } from './session-ended';

// Type definitions
export interface Typo3User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  [key: string]: unknown;
}

export interface Typo3SessionResponse {
  organizationName?: string | null;
  authenticated: boolean;
  user?: Typo3User;
  csrfToken?: string;
}

export type SessionStatus =
  'authenticated' | 'unauthenticated' | 'ended' | 'error';

export interface Typo3AuthServiceOptions {
  typo3BaseUrl?: string;
}

class Typo3AuthService {
  private typo3BaseUrl: string;
  private sessionEndpoint: string;
  private loginUrl: string;
  private logoutUrl: string;
  private authenticated: boolean;
  private user: Typo3User | null;
  private csrfToken: string | null;
  private sessionEnd: SessionEndInfo | null = null;
  /** The session check in flight for refreshSession() (single-flight, #113). */
  private refreshing: Promise<boolean> | null = null;
  private organizationName: string | null = null;

  /**
   * @param options Configuration options
   */
  constructor(options: Typo3AuthServiceOptions = {}) {
    this.typo3BaseUrl = options.typo3BaseUrl || 'https://www.gitterdan.ai';
    this.sessionEndpoint = `${this.typo3BaseUrl}/index.php?type=3001`;
    this.loginUrl = `${this.typo3BaseUrl}/index.php?type=2021`;
    this.logoutUrl = `${this.typo3BaseUrl}/index.php?type=2023`;

    this.authenticated = false;
    this.user = null;
    this.csrfToken = null; // SECURITY: Store CSRF token for protected requests
  }

  /**
   * Validate the TYPO3 session and report *why* it failed.
   *
   *  - 'authenticated'   — valid session; user + CSRF token are cached.
   *  - 'unauthenticated' — server answered but there is no session (or 401).
   *  - 'ended'           — a session existed and was ended (401 SESSION_ENDED
   *                        with a reason other than NO_SESSION); see getSessionEnd().
   *  - 'error'           — network failure or unexpected status; the caller
   *                        should NOT treat this as "logged out".
   */
  async getSessionStatus(): Promise<SessionStatus> {
    logger.debug('Validating session with TYPO3:', this.sessionEndpoint);

    try {
      const response = await fetch(this.sessionEndpoint, {
        method: 'GET',
        credentials: 'include', // Send cookies cross-origin
        headers: {
          Accept: 'application/json',
        },
      });

      logger.debug('TYPO3 validation response:', response.status);

      if (response.ok) {
        const data: Typo3SessionResponse = await response.json();
        if (data.authenticated) {
          this.authenticated = true;
          this.sessionEnd = null;
          this.organizationName =
            typeof data.organizationName === 'string' &&
            data.organizationName !== ''
              ? data.organizationName
              : null;
          this.user = data.user || null;
          this.csrfToken =
            response.headers.get('X-CSRF-Token') || data.csrfToken || null;
          logger.info('TYPO3 Authentication successful:', this.user);
          return 'authenticated';
        }
        this.sessionEnd = null;
        this.clearSession();
        return 'unauthenticated';
      }

      if (response.status === 401) {
        // Read the body and record the end first, then clear: no window in
        // which the token is gone but the session end is not yet known.
        const body = await readErrorBody(response);
        if (body?.code === 'SESSION_ENDED' && body.reason !== 'NO_SESSION') {
          this.sessionEnd = {
            kind: body.kind === 'access_ended' ? 'access_ended' : 'expired',
            reason: typeof body.reason === 'string' ? body.reason : 'UNKNOWN',
          };
          logger.warn('Session ended:', this.sessionEnd.reason);
          this.clearSession();
          return 'ended';
        }
        // A later result replaces an earlier end's reason (#113).
        this.sessionEnd = null;
        this.clearSession();
        logger.warn(
          'Session validation failed: Unauthorized (no valid session)'
        );
        return 'unauthenticated';
      }

      logger.warn('Session validation failed:', response.status);
      this.sessionEnd = null;
      this.clearSession();
      return 'error';
    } catch (error) {
      logger.error('Session validation error:', error);
      this.sessionEnd = null;
      this.clearSession();
      return 'error';
    }
  }

  /** Reset cached auth state (SECURITY: clears CSRF token). */
  private clearSession(): void {
    this.authenticated = false;
    this.user = null;
    this.csrfToken = null;
  }

  /**
   * Redirect to TYPO3 login page
   *
   * After successful login, TYPO3 will redirect back to IMSight base URL.
   * Since IMSight is an SPA, client-side routing will handle the rest.
   */
  redirectToLogin(language?: string): void {
    const url = this.getLoginUrl(language);
    logger.info('Redirecting to TYPO3 login:', url);
    window.location.href = url;
  }

  /**
   * Login entry for the UI language (#107). TYPO3 has English (default) and
   * German; the German entry makes the pending / invite pages German too.
   * Every other UI language falls back to English. The German entry is the
   * routed language root `/de/?type=…` — TYPO3 answers 404 for `/de/index.php`.
   */
  getLoginUrl(language?: string): string {
    return language === 'de'
      ? `${this.typo3BaseUrl}/de/?type=2021`
      : this.loginUrl;
  }

  /** Redirect to the TYPO3 logout (#108: in the UI language, like login). */
  redirectToLogout(language?: string): void {
    const url = this.getLogoutUrl(language);
    logger.info('Redirecting to TYPO3 logout:', url);
    window.location.href = url;
  }

  /**
   * Logout entry for the UI language (#108). The backend remembers the language
   * for the signed-out page; TYPO3 has English (default) and German.
   */
  getLogoutUrl(language?: string): string {
    return language === 'de'
      ? `${this.typo3BaseUrl}/de/?type=2023`
      : this.logoutUrl;
  }

  /**
   * Check if user is authenticated
   *
   * @returns True if authenticated
   */
  isAuthenticated(): boolean {
    return this.authenticated;
  }

  /**
   * Get current user data
   *
   * @returns User object or null if not authenticated
   */
  getUser(): Typo3User | null {
    return this.user;
  }

  /**
   * Get user full name
   *
   * @returns Full name or null if not authenticated
   */
  getUserName(): string | null {
    if (!this.user) return null;

    const firstName = this.user.firstName || '';
    const lastName = this.user.lastName || '';

    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }

    return this.user.email || null;
  }

  /**
   * Get CSRF token for making protected requests
   *
   * @returns CSRF token or null if not available
   */
  getCsrfToken(): string | null {
    return this.csrfToken;
  }

  /**
   * Re-validate the session and refresh the cached CSRF token (called after a
   * 403 CSRF rejection). A session that turns out to be gone is reported to the
   * session-ended notifier (imsight#106); a network error is not.
   */
  refreshSession(): Promise<boolean> {
    // Several requests rejected at once share one check (#113).
    if (!this.refreshing) {
      this.refreshing = this.checkAndReport().finally(() => {
        this.refreshing = null;
      });
    }
    return this.refreshing;
  }

  private async checkAndReport(): Promise<boolean> {
    const status = await this.getSessionStatus();
    if (status === 'ended') {
      notifySessionEnded(this.sessionEnd!);
    } else if (status === 'unauthenticated') {
      notifySessionEnded({ kind: 'expired', reason: 'NO_SESSION' });
    }
    return status === 'authenticated';
  }

  /** Why the session ended (status 'ended'), else null. */
  getSessionEnd(): SessionEndInfo | null {
    return this.sessionEnd;
  }

  /** Organization name from the last successful session check, kept after a loss. */
  getOrganizationName(): string | null {
    return this.organizationName;
  }
}

// Export to window for debugging (dev only)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.Typo3AuthService = Typo3AuthService;
}

export default Typo3AuthService;
